/* Shto SCP PDP buy box: gallery (swipe, progress bar, thumbnails), variant picker, quantity, and the variant contract.
   Variants come from the JSON in the section. On every variant change: the picked label(s), the [option] word in the heading,
   price, the form's id input (+ a change event, which Shopify's installments banner listens to), button state, the gallery,
   and document event `shto:pdp-variant` (build-notes "Variant contract"). Add to cart is the theme's <product-form>.
   The address is never changed (no ?variant=, no #). */
(function () {
  function ensureProductForm(root) {
    if (customElements.get('product-form')) return;
    var holder = root.querySelector('[data-shto-pdp-productjs]');
    if (!holder || document.querySelector('script[data-shto-productjs]')) return;
    var s = document.createElement('script');
    s.src = holder.getAttribute('data-shto-pdp-productjs');
    s.defer = true;
    s.setAttribute('data-shto-productjs', '');
    document.head.appendChild(s);
  }

  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }

  function init(root) {
    if (root.dataset.shtoPdpOn) return;
    root.dataset.shtoPdpOn = '1';
    ensureProductForm(root);

    var variants = [];
    try { variants = JSON.parse(root.querySelector('[data-shto-pdp-variants]').textContent); } catch (e) { variants = []; }
    var hideSoldout = root.dataset.hideSoldout === 'true';
    var mode = root.dataset.mode;

    /* ---------- Gallery ---------- */
    var track = root.querySelector('[data-shto-pdp-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-shto-pdp-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-shto-pdp-thumb]'));
    var progress = root.querySelector('[data-shto-pdp-progress]');
    var current = 0;

    function index() { return track && track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0; }
    function paint() {
      if (!track) return;
      var pos = track.clientWidth ? track.scrollLeft / track.clientWidth : 0;
      if (progress) progress.style.setProperty('--shto-pdp-pos', Math.max(0, Math.min(slides.length - 1, pos)).toFixed(4));
      var i = index();
      if (i === current) return;
      current = i;
      thumbs.forEach(function (t, k) {
        t.classList.toggle('is-current', k === i);
        if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
    }
    function goTo(i, instant) {
      if (!track || !slides[i]) return;
      var left = i * track.clientWidth;
      if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) track.scrollLeft = left;
      else track.scrollTo({ left: left, behavior: 'smooth' });
      if (instant) paint();
    }
    if (track) {
      track.addEventListener('scroll', function () { window.requestAnimationFrame(paint); }, { passive: true });
      window.addEventListener('resize', function () { goTo(current, true); });
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(slides.length - 1, index() + 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(0, index() - 1)); }
      });
    }
    thumbs.forEach(function (t, k) { t.addEventListener('click', function () { goTo(k); }); });

    // Slide 1 can show a variant's own store photo (photo blocks), and goes back to its own photo when that's wanted.
    var first = slides[0];
    var firstImg = first ? first.querySelector('img') : null;
    var firstThumb = thumbs[0] ? thumbs[0].querySelector('img') : null;
    // Variant photos are fetched ahead (after the page is idle, one at a time) as detached images with the same srcset and
    // sizes as slide 1, so the browser picks the same file; the swap waits for decode so it paints in the next frame.
    var loads = {};
    function load(src, srcset, urgent) {
      if (!src) return null;
      if (loads[src]) return loads[src];
      var im = new Image();
      im.decoding = 'async';
      if (!urgent && 'fetchPriority' in im) im.fetchPriority = 'low';
      if (firstImg) im.sizes = firstImg.getAttribute('sizes') || '';
      if (srcset) im.srcset = srcset;
      im.src = src;
      var pr = (im.decode ? im.decode() : Promise.reject()).catch(function () {
        return new Promise(function (res) { if (im.complete) res(); else { im.onload = res; im.onerror = res; } });
      });
      loads[src] = pr;
      return pr;
    }
    var wantFirst = null;
    function setFirst(src, srcset, alt, thumb) {
      if (!firstImg || !src || firstImg.getAttribute('src') === src) { wantFirst = src; return; }
      wantFirst = src;
      (load(src, srcset, true) || Promise.resolve()).then(function () {
        if (wantFirst !== src) return;
        if (srcset) firstImg.setAttribute('srcset', srcset); else firstImg.removeAttribute('srcset');
        firstImg.setAttribute('src', src);
        firstImg.setAttribute('alt', alt || '');
        if (firstThumb && thumb) firstThumb.setAttribute('src', thumb);
      });
    }
    function idle(fn) { if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2000 }); else setTimeout(fn, 200); }
    // After the first paint: the gallery's other slides load (eager, low priority), then the variant photos one by one.
    function warm() {
      if (navigator.connection && navigator.connection.saveData) return;
      slides.forEach(function (sl, k) {
        var im = sl.querySelector('img');
        if (k > 0 && im && im.loading === 'lazy') { if ('fetchPriority' in im) im.fetchPriority = 'low'; im.loading = 'eager'; }
      });
      if (mode !== 'blocks') return;
      var seen = {}, queue = [];
      if (first) seen[first.dataset.oSrc] = 1;
      if (firstImg) seen[firstImg.getAttribute('src')] = 1;
      variants.forEach(function (v) { if (v.src && !seen[v.src]) { seen[v.src] = 1; queue.push(v); } });
      (function next() {
        var v = queue.shift();
        if (!v) return;
        (load(v.src, v.srcset, false) || Promise.resolve()).then(function () { idle(next); });
      })();
    }
    if (document.readyState === 'complete') idle(warm); else window.addEventListener('load', function () { idle(warm); });
    function showVariantMedia(v) {
      if (!slides.length) return;
      if (mode === 'blocks') {
        var fl = norm(v.options[0]), at = -1;
        slides.forEach(function (sl, k) { if (at < 0 && sl.dataset.flavor && sl.dataset.flavor === fl) at = k; });
        if (at >= 0) {
          if (at === 0 && first) setFirst(first.dataset.oSrc, first.dataset.oSrcset, first.dataset.oAlt, first.dataset.oThumb);
          goTo(at);
        } else if (v.src) {
          setFirst(v.src, v.srcset, v.alt, v.thumb);
          goTo(0);
        }
      } else if (v.mediaId) {
        slides.forEach(function (sl, k) { if (String(sl.dataset.mediaId) === String(v.mediaId)) goTo(k); });
      }
    }

    /* ---------- Variant picker ---------- */
    var idInput = root.querySelector('[data-shto-pdp-id]');
    var priceEl = root.querySelector('[data-shto-pdp-price]');
    var compareEl = root.querySelector('[data-shto-pdp-compare]');
    var titleEl = root.querySelector('[data-shto-pdp-title]');
    var optionWords = Array.prototype.slice.call(root.querySelectorAll('[data-shto-pdp-option]'));
    var add = root.querySelector('[data-shto-pdp-add]');
    var addLabel = root.querySelector('[data-shto-pdp-add-label]');
    var checkout = root.querySelector('[data-shto-pdp-checkout]');
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-shto-pdp-value-btn]'));
    var rows = {};
    buttons.forEach(function (b) { (rows[b.dataset.option] = rows[b.dataset.option] || []).push(b); });

    var currentVariant = variants.filter(function (v) { return idInput && String(v.id) === idInput.value; })[0] || variants[0];
    var selected = currentVariant ? currentVariant.options.slice() : [];

    function resolve(i, value) {
      var want = selected.slice(); want[i] = value;
      var exact = variants.filter(function (v) { return v.options.every(function (o, k) { return o === want[k]; }); })[0];
      if (exact && (exact.available || !hideSoldout)) return exact;
      // That combination doesn't exist (or is hidden): the variant with this value that keeps most of the other picks,
      // in stock first.
      var best = null, bestScore = -1;
      variants.forEach(function (v) {
        if (v.options[i] !== value) return;
        if (hideSoldout && !v.available) return;
        var score = (v.available ? 100 : 0);
        v.options.forEach(function (o, k) { if (k !== i && o === want[k]) score += 10 - k; });
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best || exact;
    }

    function paintOptions() {
      buttons.forEach(function (b) {
        var i = +b.dataset.option, val = b.dataset.value;
        var on = selected[i] === val;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        var match = variants.filter(function (v) {
          return v.options[i] === val && v.options.every(function (o, k) { return k === i || o === selected[k]; });
        })[0];
        var ok = !!(match && match.available);
        b.classList.toggle('is-soldout', !ok);
        if (hideSoldout && b.dataset.anyAvailable !== 'true') b.hidden = true;
      });
      Object.keys(rows).forEach(function (i) {
        var lab = root.querySelector('[data-shto-pdp-value="' + i + '"]');
        if (lab) lab.textContent = selected[i] || '';
      });
    }

    function apply(v, fromUser) {
      if (!v) return;
      currentVariant = v;
      selected = v.options.slice();
      paintOptions();
      optionWords.forEach(function (w) { w.textContent = v.options[0]; });
      if (priceEl) priceEl.innerHTML = v.priceHtml;
      if (compareEl) { compareEl.innerHTML = v.compareHtml || ''; compareEl.hidden = !v.compareHtml; }
      if (idInput && idInput.value !== String(v.id)) {
        idInput.value = v.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // Shopify's installments banner also takes the variant as an attribute (it re-reads it when that changes).
      root.querySelectorAll('shopify-payment-terms').forEach(function (t) { if (t.getAttribute('variant-id') !== String(v.id)) t.setAttribute('variant-id', v.id); });
      // Light Labs' widget (the Custom Liquid block) takes the page's variant from ShopifyAnalytics, which reads the last
      // add-to-cart form on the page (a "Pair it with" card on our templates). Its own external-id attribute gives it ours.
      // Per-flavor Light Labs report (products with flavor variants): the widget reads product-id only when it starts, so a
      // new widget with the picked flavor's report replaces it.
      root.querySelectorAll('[data-shto-lab-map]').forEach(function (box) {
        var map; try { map = JSON.parse(box.getAttribute('data-shto-lab-map')); } catch (e) { return; }
        var want = map[String(v.id)], w = box.querySelector('lightlabs-widget');
        if (!want || !w || w.getAttribute('product-id') === want) return;
        var n = document.createElement('lightlabs-widget');
        Array.prototype.forEach.call(w.attributes, function (a) { n.setAttribute(a.name, a.value); });
        n.setAttribute('product-id', want);
        w.parentNode.replaceChild(n, w);
      });
      root.querySelectorAll('lightlabs-widget').forEach(function (w) { if (w.getAttribute('external-id') !== String(v.id)) w.setAttribute('external-id', v.id); });
      if (add) {
        add.disabled = !v.available;
        if (addLabel) addLabel.textContent = v.available ? add.dataset.label : add.dataset.soldout;
      }
      if (checkout) checkout.hidden = !v.available;
      if (fromUser) {
        showVariantMedia(v);
        var shownImg = slides[0] && slides[0].querySelector('img');
        document.dispatchEvent(new CustomEvent('shto:pdp-variant', {
          detail: {
            variantId: v.id,
            available: v.available,
            price: v.price,
            priceHtml: v.priceHtml,
            title: titleEl ? titleEl.textContent.trim() : '',
            image: v.src || (shownImg ? shownImg.currentSrc || shownImg.src : null) || null
          }
        }));
      }
    }

    function warmBtn(b) {
      var v = resolve(+b.dataset.option, b.dataset.value);
      if (v && v.src) load(v.src, v.srcset, true);
    }
    buttons.forEach(function (b) {
      b.addEventListener('pointerenter', function () { warmBtn(b); });
      b.addEventListener('touchstart', function () { warmBtn(b); }, { passive: true });
      b.addEventListener('click', function () {
        var v = resolve(+b.dataset.option, b.dataset.value);
        if (v && v !== currentVariant) apply(v, true);
      });
      b.addEventListener('keydown', function (e) {
        var step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        var list = rows[b.dataset.option].filter(function (x) { return !x.hidden; });
        var next = list[(list.indexOf(b) + step + list.length) % list.length];
        next.focus();
        next.click();
      });
    });
    if (currentVariant) apply(currentVariant, false);

    // Start on the variant's own slide (a product opened with its variant photo elsewhere in the media).
    if (mode === 'media' && currentVariant && currentVariant.mediaId) {
      slides.forEach(function (sl, k) { if (k > 0 && String(sl.dataset.mediaId) === String(currentVariant.mediaId)) goTo(k, true); });
    }
    paint();

    /* ---------- Quantity ---------- */
    var qty = root.querySelector('[data-shto-pdp-qty-input]');
    root.querySelectorAll('[data-shto-pdp-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qty) return;
        var step = +qty.step || 1, min = +qty.min || 1, max = qty.max ? +qty.max : Infinity;
        var next = (parseInt(qty.value, 10) || min) + step * +btn.dataset.shtoPdpQty;
        qty.value = Math.max(min, Math.min(max, next));
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    if (qty) qty.addEventListener('change', function () {
      var min = +qty.min || 1, max = qty.max ? +qty.max : Infinity, n = parseInt(qty.value, 10);
      if (isNaN(n)) n = min;
      qty.value = Math.max(min, Math.min(max, n));
    });

    /* ---------- One click = one add ---------- */
    // A second submit while the first is on its way is dropped before the theme's handler sees it. Both listeners sit on the
    // form, so only stopImmediatePropagation keeps the theme's from running.
    var form = root.querySelector('product-form form');
    if (form && add) {
      var busy = false;
      var done = function () { busy = false; add.classList.remove('is-busy'); add.removeAttribute('aria-busy'); };
      form.addEventListener('submit', function (e) {
        if (busy || add.disabled) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        busy = true;
        add.classList.add('is-busy');
        add.setAttribute('aria-busy', 'true');
        setTimeout(done, 8000);
      }, true);
      document.addEventListener('cart:item-added', done);
      document.addEventListener('product:variant-error', done);
    }
  }

  // Flavor links on single-flavor products: a preview (theme preview, ?view= template) stays a preview on the next flavor.
  function carryPreview(root) {
    var q = new URLSearchParams(window.location.search), keep = [];
    ['preview_theme_id', 'pb', 'view'].forEach(function (k) { if (q.has(k)) keep.push([k, q.get(k)]); });
    // Shopify drops preview_theme_id from the address once the preview starts: take it from the theme being shown.
    var th = window.Shopify && window.Shopify.theme;
    if (!q.has('preview_theme_id') && th && th.id && th.role && th.role !== 'main') keep.unshift(['preview_theme_id', String(th.id)]);
    if (!keep.length) return;
    root.querySelectorAll('a[data-shto-pdp-flavor-link]').forEach(function (a) {
      var u = new URL(a.getAttribute('href'), window.location.origin);
      keep.forEach(function (kv) { u.searchParams.set(kv[0], kv[1]); });
      a.setAttribute('href', u.pathname + u.search);
    });
  }

  function boot() { document.querySelectorAll('[data-shto-pdp-buy]').forEach(function (r) { carryPreview(r); init(r); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
